import Account from "byzantine/src/Account";
import { TruncatedAccount } from "@narmi/design_system";
import { memo } from "react";
import { useLocalization } from "@fluent/react";
import { groupAccounts } from "byzantine/src/AccountUtils";
import { useInstitution } from "byzantine/src/dbbl/businessLogic/entities";
import { RenderItemMenuProps } from "../types";
import Details from "../../Details";

type Props = {
  item: RenderItemMenuProps<API.AccountMenuItem>;
  orderedAccounts: Account[];
};

const AccountItem = ({ item, orderedAccounts }: Props) => {
  const { l10n } = useLocalization();

  const accounts = orderedAccounts.filter((a) => a.isInternal());

  // Group using default English product groups, translate at display time
  const groupedAccounts = groupAccounts(accounts);
  const numGroups = Array.from(groupedAccounts).length;
  const institution = useInstitution();
  const entryPoints = institution?.account_opening_entry_points_config?.accounts_menu ?? [];

  const getDisplayGroupName = (groupName: string): string => {
    const groupTranslationMap: Record<string, string> = {
      Favorites: l10n.getString("account-group-favorites"),
      Checking: l10n.getString("account-group-checking"),
      Savings: l10n.getString("account-group-savings"),
      "Credit cards": l10n.getString("account-group-credit-cards"),
      Loans: l10n.getString("account-group-loans"),
      CDs: l10n.getString("account-group-cds"),
      Others: l10n.getString("account-group-others"),
      "External accounts": l10n.getString("account-group-external-accounts"),
    };
    return groupTranslationMap[groupName] || groupName;
  };

  return (
    <Details
      label={item.name}
      key={item.name}
      type="wide details responsive"
      ariaLabel={`${item.name} menu`}
    >
      {Array.from(groupedAccounts, ([groupName, accountsForGroup]) => (
        <div
          key={groupName}
          className={`account-group navbar-accountGroupColumn--${numGroups}`}
          role="menu"
        >
          <div className="group-header">{getDisplayGroupName(groupName)}</div>
          {accountsForGroup.map((account) => (
            <div key={account.id}>
              <a
                className="topMenu-button fontWeight--default fontColor--primary"
                href={`/accounts/${account.id}`}
                role="menuitem"
              >
                <TruncatedAccount
                  name={account.name}
                  lastFour={
                    account.isInternalLoan()
                      ? l10n.getString(
                          "dashboard-account-masked-number",
                          {
                            accountNumber: account.number || "",
                          },
                          account.getMaskedNumber()
                        )
                      : account.getMaskedNumber()
                  }
                />
              </a>
            </div>
          ))}
        </div>
      ))}
      {entryPoints?.length > 0 && (
        <div
          key="open_an_account"
          className={`account-group navbar-accountGroupColumn--${numGroups}`}
          role="menu"
        >
          <div className="group-header">
            {l10n.getString("heading-open-an-account")}
          </div>
          {entryPoints?.map((entryPoint) => (
            <div key={entryPoint.target}>
              <a
                className="topMenu-button fontWeight--default fontColor--primary"
                href={`/v1/entry_points?platform=${
                  entryPoint.platform
                }&selected=${encodeURIComponent(entryPoint.target)}`}
                role="menuitem"
              >
                {entryPoint.name}
              </a>
            </div>
          ))}
        </div>
      )}
    </Details>
  );
};

export default memo(AccountItem);
